import React, { memo, forwardRef } from "react";
import {
  Avatar,
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  IconButton,
  ListItemButton,
  ListItemText,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { Virtuoso } from "react-virtuoso";

export const LIST_PANEL_MIN_H = 300;
export const LIST_PANEL_MAX_H = 420;
export const CONVERSATION_ROW_HEIGHT = 88;

function statusLabel(code) {
  const map = {
    0: "Pendente",
    1: "Em Atendimento",
    2: "Concluída",
    3: "Contato Futuro",
    4: "Perdida",
    5: "Cancelada",
  };
  const n = typeof code === "string" ? Number(code) : code;
  return map[n] ?? "—";
}

const ConversationRowItem = memo(function ConversationRowItem({
  c,
  onOpen,
  t,
  showWorkspace = false,
}) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "stretch",
        height: CONVERSATION_ROW_HEIGHT,
        boxSizing: "border-box",
        borderBottom: 1,
        borderColor: "divider",
      }}
    >
      <ListItemButton
        onClick={() => onOpen(c)}
        sx={{ flex: 1, minWidth: 0, py: 0.75, pr: 0.5, alignItems: "flex-start" }}
      >
        <ListItemText
          primary={
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
              <Typography component="span" variant="body2" fontWeight={600} noWrap sx={{ maxWidth: "100%" }}>
                {c.name || `#${c.id}`}
              </Typography>
              {Number(c.unread) > 0 ? (
                <Chip size="small" label={c.unread} color="warning" sx={{ height: 22, flexShrink: 0 }} />
              ) : null}
            </Box>
          }
          secondary={
            <Box component="span" sx={{ display: "block", overflow: "hidden" }}>
              <Typography component="span" variant="caption" color="text.secondary" display="block" noWrap>
                {showWorkspace && c.dominio ? `${c.dominio} · ` : ""}
                {c.channel || c.channelType || "—"} · {statusLabel(c.status)}
                {c.assignedTo ? ` · ${c.assignedTo}` : ""}
                {c.agentName ? ` · ${c.agentName}` : ""}
              </Typography>
            </Box>
          }
          primaryTypographyProps={{ component: "div" }}
          secondaryTypographyProps={{ component: "div" }}
        />
      </ListItemButton>
      <Box sx={{ display: "flex", alignItems: "flex-start", pt: 0.5, pr: 0.25, flexShrink: 0 }}>
        <Tooltip title={t("dashboardPage.live.openChat", { defaultValue: "Abrir atendimento" })}>
          <IconButton
            size="small"
            onClick={() => onOpen(c)}
            aria-label={t("dashboardPage.live.openChat", { defaultValue: "Abrir atendimento" })}
          >
            <OpenInNewIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
});

export function LiveConversationList({ items, emptyLabel, onOpen, t, showWorkspace = false }) {
  if (!items?.length) {
    return (
      <Typography color="text.secondary" variant="body2" sx={{ py: 1 }}>
        {emptyLabel}
      </Typography>
    );
  }
  return (
    <Virtuoso
      data={items}
      style={{ height: "100%", width: "100%" }}
      defaultItemHeight={CONVERSATION_ROW_HEIGHT}
      fixedItemHeight={CONVERSATION_ROW_HEIGHT}
      overscan={{ main: 12, reverse: 12 }}
      computeItemKey={(_, c) => `${c.dominio_id || ""}:${c.id}`}
      context={{ onOpen, t, showWorkspace }}
      itemContent={(_index, c, ctx) => (
        <ConversationRowItem c={c} onOpen={ctx.onOpen} t={ctx.t} showWorkspace={ctx.showWorkspace} />
      )}
    />
  );
}

export function LiveKpiCard({ loading, icon, title, value, color, sub, onAi, tChat }) {
  if (loading) {
    return (
      <Grid item xs={12} sm={6} md={3} sx={{ display: "flex" }}>
        <Skeleton variant="rounded" height={118} sx={{ width: "100%", borderRadius: 1 }} />
      </Grid>
    );
  }
  return (
    <Grid item xs={12} sm={6} md={3} sx={{ display: "flex" }}>
      <Card
        elevation={1}
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          border: 1,
          borderColor: "divider",
          boxShadow: (th) =>
            th.palette.mode === "dark"
              ? `0 1px 4px ${alpha("#000", 0.35)}`
              : `0 1px 3px ${alpha(th.palette.common.black, 0.08)}`,
        }}
      >
        <CardContent sx={{ flex: 1 }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar sx={{ bgcolor: color, width: 44, height: 44 }}>{icon}</Avatar>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Stack direction="row" alignItems="center" spacing={0.75}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ flex: 1, minWidth: 0 }} noWrap>
                  {title}
                </Typography>
                {onAi ? (
                  <Tooltip title={tChat("analytics.explainAi")}>
                    <IconButton size="small" onClick={onAi} aria-label={tChat("analytics.explainAiAria")}>
                      <AutoAwesomeIcon fontSize="inherit" />
                    </IconButton>
                  </Tooltip>
                ) : null}
              </Stack>
              <Typography variant="h4" sx={{ color, lineHeight: 1.15, fontWeight: 600 }}>
                {value}
              </Typography>
              {sub ? (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
                  {sub}
                </Typography>
              ) : null}
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </Grid>
  );
}

export function LiveListCard({ title, children, footer, onAi, tChat, sampleHint }) {
  return (
    <Grid item xs={12} lg={6} sx={{ display: "flex" }}>
      <Card
        elevation={1}
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          border: 1,
          borderColor: "divider",
          boxShadow: (th) =>
            th.palette.mode === "dark"
              ? `0 1px 4px ${alpha("#000", 0.35)}`
              : `0 1px 3px ${alpha(th.palette.common.black, 0.08)}`,
        }}
      >
        <CardContent sx={{ flex: 1, display: "flex", flexDirection: "column", pt: 2, pb: 2, px: 2 }}>
          <Stack direction="row" alignItems="flex-start" spacing={1} sx={{ mb: 1 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                {title}
              </Typography>
            </Box>
            {onAi ? (
              <Tooltip title={tChat("analytics.explainAi")}>
                <IconButton size="small" onClick={onAi} aria-label={tChat("analytics.explainAiAria")}>
                  <AutoAwesomeIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : null}
          </Stack>
          {sampleHint ? (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.75, lineHeight: 1.4 }}>
              {sampleHint}
            </Typography>
          ) : null}
          <Divider />
          <Box
            sx={{
              flex: 1,
              height: LIST_PANEL_MAX_H,
              minHeight: LIST_PANEL_MIN_H,
              overflow: "hidden",
              mt: 1,
            }}
          >
            {children}
          </Box>
          {footer ? <Box sx={{ mt: 1, flexShrink: 0 }}>{footer}</Box> : null}
        </CardContent>
      </Card>
    </Grid>
  );
}
